<template>
  <form
    :value="isValid"
    @submit.prevent="onSubmit"
  >
    <pp-row>
      <pp-col
        cols="12"
        md="6"
        class="pb-0 pb-md-3"
      >
        <pp-input-text
          class="mb-5"
          label="First Name"
          v-model="firstName"
          @blur="onBlur('firstName')"
          :error-messages="firstNameErrors"
          :disabled="isLoading"
        />
        <pp-input-text
          label="Last Name"
          class="mb-5"
          v-model="lastName"
          @blur="onBlur('lastName')"
          :error-messages="lastNameErrors"
          :disabled="isLoading"
        />
        <pp-input-text
          label="Email"
          class="mb-5"
          v-model="email"
          @blur="onBlur('email')"
          :error-messages="emailErrors"
          :disabled="isLoading"
          type="email"
        />
        <pp-input-text
          label="Phone Number"
          class="mb-5"
          v-model="phone"
          @blur="onBlur('phone')"
          :error-messages="phoneErrors"
          :disabled="isLoading"
          v-mask="'(###) ### ####'"
          type="tel"
        />
      </pp-col>
      <pp-col
        cols="12"
        md="6"
        class="pt-0 pt-md-3"
      >
        <pp-input-textarea
          label="Comments"
          class="mb-5"
          v-model="comments"
          @blur="onBlur('comments')"
          :error-messages="commentsErrors"
          :disabled="isLoading"
        />
        <pp-input-checkbox
          label="I understand and agree that any information submitted will be forwarded to our office by email and not via a secure messaging system. This form should not be used to transmit private health information, and we disclaim all warranties with respect to the privacy and confidentiality of any information submitted through this form."
          :error-messages="termsAcceptedErrors"
          :disabled="isLoading"
          v-model="termsAccepted"
          @change="onBlur('termsAccepted')"
          class="mb-5"
        />
        <pp-input-checkbox
          :label="smsCopy"
          :error-messages="smsTermsAcceptedErrors"
          :disabled="isLoading"
          v-model="smsTermsAccepted"
          @change="onBlur('smsTermsAccepted')"
          class="mb-5"
        />
        <div>
          <vue-recaptcha
            class="mb-5"
            :load-recaptcha-script="true"
            :sitekey="recaptchaSiteKey"
            ref="recaptcha"
            @verify="onRecaptchaVerification"
            @expired="onRecaptchaExpiration"
          />
          <p
            class="v-messages red--text pl-3 mb-4 recaptcha-error-message"
            v-if="showRecaptchaError"
          >
            Please verify you are human
          </p>
        </div>
      </pp-col>
    </pp-row>
    <pp-button
      text="SEND"
      type="primary"
      :loading="isLoading"
      :submit="true"
      class="d-block mx-auto"
    />
    <pp-snack-message
      v-model="showSnackbar"
      :color="snackbarColor"
      :absolute="true"
      :bottom="true"
      :timeout="2000"
      @input="onSnackbarReset"
    >
      {{ snackbarMessage }}
    </pp-snack-message>
  </form>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha'
import { VueMaskDirective } from 'v-mask'
import { validationMixin } from 'vuelidate'
import { required, email, sameAs, helpers } from 'vuelidate/lib/validators'
import WebsitesDomainServiceClient from '@/clients/WebsitesDomainServiceClient'
import { RECAPTCHA_SITE_KEY, UA_TO_GA4_ACTION_MAPPING } from '@/constants'

const phoneRegex = /^\(\d{3}\) \d{3} \d{4}$/
const phone = helpers.regex('phone', phoneRegex)

export default {
  name: 'ContactUsForm',
  components: {
    'vue-recaptcha': VueRecaptcha
  },
  directives: {
    mask: VueMaskDirective
  },
  mixins: [validationMixin],
  props: {
    practiceUuid: {
      type: String,
      required: true
    },
    practiceName: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      isLoading: false,
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
      comments: null,
      termsAccepted: false,
      smsTermsAccepted: false,
      showRecaptchaError: false,
      recaptchaSiteKey: RECAPTCHA_SITE_KEY,
      token: null,
      client: new WebsitesDomainServiceClient(this.practiceUuid),
      showSnackbar: false,
      snackbarColor: null,
      snackbarMessage: null
    }
  },
  computed: {
    isValid () {
      return !this.$v.$invalid && !!this.token
    },
    smsCopy () {
      return `By submitting this form, you consent to receive SMS messages from ${this.practiceName || 'the practice'}. Please note that your number will not be shared with third parties or affiliates for marketing purposes under any circumstances.`;
    },
    firstNameErrors () {
      return this.generateRequiredFieldErrors('firstName', 'First name')
    },
    lastNameErrors () {
      return this.generateRequiredFieldErrors('lastName', 'Last name')
    },
    emailErrors () {
      const requiredErrors = this.generateRequiredFieldErrors('email', 'Email')

      if (requiredErrors.length) {
        return requiredErrors
      }
      if (this.$v.email.$dirty && !this.$v.email.email) {
        return ['Please enter a valid email address']
      }
      return []
    },
    phoneErrors () {
      const requiredErrors = this.generateRequiredFieldErrors('phone', 'Phone number')

      if (requiredErrors.length) {
        return requiredErrors
      }
      if (this.$v.phone.$dirty && !this.$v.phone.phone) {
        return ['Please enter a valid phone number']
      }
      return []
    },
    commentsErrors () {
      return this.generateRequiredFieldErrors('comments', 'A comment')
    },
    termsAcceptedErrors () {
      return this.$v.termsAccepted.$dirty && this.$v.termsAccepted.$invalid ?
        ['Please accept the terms and conditions'] : []
    },
    smsTermsAcceptedErrors () {
      return this.$v.smsTermsAccepted.$dirty && this.$v.smsTermsAccepted.$invalid ?
        ['Please accept the SMS terms and conditions'] : []
    }
  },
  methods: {
    generateRequiredFieldErrors (fieldKey, errorMessagePrefix) {
      return this.$v[fieldKey].$dirty && !this.$v[fieldKey].required
        ? [`${errorMessagePrefix} is required`] : []
    },
    showValidationErrors () {
      if (!this.token) {
        this.showRecaptchaError = true
      }
      this.$v.$touch()
    },
    resetForm () {
      this.$refs.recaptcha.reset()
      this.$v.$reset()

      this.firstName = null
      this.lastName = null
      this.email = null
      this.phone = null
      this.comments = null
      this.termsAccepted = false
      this.smsTermsAccepted = false
    },
    onRecaptchaVerification (token) {
      this.token = token
      this.showRecaptchaError = false
    },
    onRecaptchaExpiration () {
      this.token = null
      this.showRecaptchaError = true
    },
    onSnackbarReset () {
      this.snackbarColor = null
      this.snackbarMessage = null
    },
    onBlur (field) {
      this.$v[field].$touch()
      if (this.$v[field].$invalid) {
        this.track({ action: 'form-input-error', label: field})
        return
      }

      this.track({ action: 'form-input', label: field })
    },
    onSubmit () {
      if (!this.isValid) {
        this.showValidationErrors()
        this.track({ action: 'submit-with-errors' })
        return
      }

      this.isLoading = true
      this.submitContactUsForm({
        name: `${this.firstName} ${this.lastName}`.trim(),
        email: this.email,
        phone: this.phone,
        comments: this.comments,
        practiceName: this.practiceName
      })
    },
    submitContactUsForm (emailData) {
      this.track({ action: 'submit' })

      this.client.submitContactUsForm(emailData, this.token)
        .then(() => {
          this.showSnackbar = true
          this.snackbarColor = 'success'
          this.snackbarMessage = 'Your message has been submitted. Thank you!'
          this.isLoading = false
          this.resetForm()
          this.track({ category: 'button', action: 'click', label: 'contact us - comment' })
        })
        .catch(() => {
          this.track({ action: 'submit-api-error' })
          this.showSnackbar = true
          this.snackbarColor = 'error'
          this.snackbarMessage = 'There was an error submitting your message. Please try again.'
          this.isLoading = false
        })
    },
    track ({ category, action, label }) {
      if (!this.$ppAnalytics) {
        return
      }

      if (this.$isGA4Enabled) {
        action = UA_TO_GA4_ACTION_MAPPING[action]
      }

      if (label) {
        this.$ppAnalytics.trackEvent({
          category,
          action,
          label
        })
      } else {
        this.$ppAnalytics.trackEvent({
          category,
          action
        })
      }
    }
  },
  validations: {
    firstName: { required },
    lastName: { required },
    email: { required, email },
    phone: { required, phone },
    comments: { required },
    termsAccepted: { accepted: sameAs(() => true) },
    smsTermsAccepted: { accepted: sameAs(() => true) }
  }
}
</script>
<style lang="scss" scoped>
.recaptcha-error-message {
  height: 1em;
}
</style>
